// apps/cli/src/commands/bootstrap/finish.ts
import * as fs from "node:fs/promises";
import * as os from "node:os";
import * as path from "node:path";
import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { LocalCliError } from "../../lib/errors.js";
import { colors } from "../../lib/output.js";
import { readBootstrapState } from "../../lib/bootstrap-state.js";
import { readStatusBlob } from "../../lib/bootstrap-status.js";
import { discoverGateway } from "../../lib/discovery.js";
import { writeConfig } from "../../lib/config.js";
import { reactiveSeedOnFinish } from "../../lib/company-profile-seed.js";
import { renderInstallSummary } from "../../lib/install-summary.js";

export class BootstrapFinishCommand extends M8tCommand {
  static paths = [["bootstrap", "finish"]];
  static usage = Command.Usage({
    description: "Point your local tools at the now-live platform (repo-root marker, discovery cache, next steps).",
    details: "Final step of `m8t bootstrap` (after the install reaches done). Writes ~/.m8t-stack/repo-root, points your local tools at the live gateway, and — if you completed the onboarding questionnaire — seeds your brain-backed advisor's brain with your company profile (best-effort; never blocks finish). Run `m8t bootstrap seed-profile` to seed manually later.",
    examples: [["Finish local", "$0 bootstrap finish --repo-root /path/to/m8t"]],
  });

  repoRoot = Option.String("--repo-root");
  subscription = Option.String("--subscription");
  resourceGroup = Option.String("--resource-group");

  async executeCommand(): Promise<number> {
    const state = await readBootstrapState();
    if (!state) {
      throw new LocalCliError({ code: "BOOTSTRAP_NO_STATE", message: "No bootstrap state.", hint: "Run 'm8t bootstrap launch' first." });
    }
    const doc = await readStatusBlob({ saName: state.statusSaName, resourceGroup: state.resourceGroup, subscriptionId: state.subscriptionId });
    if (doc.status !== "done") {
      throw new LocalCliError({
        code: "BOOTSTRAP_FINISH_NOT_DONE",
        message: `The platform is not live yet (status: ${doc.status}, phase: ${doc.phase}).`,
        hint: "Wait for 'm8t bootstrap status --watch' to reach done.",
      });
    }

    // repo-root marker (needed for persona render + deploy bicep resolution).
    const repoRoot = (typeof this.repoRoot === "string" ? this.repoRoot : undefined) ?? process.cwd();
    const markerDir = path.join(os.homedir(), ".m8t-stack");
    await fs.mkdir(markerDir, { recursive: true });
    await fs.writeFile(path.join(markerDir, "repo-root"), `${repoRoot}\n`, "utf8");

    // Discovery + config cache (best-effort: the platform is already live).
    const subscriptionId = (typeof this.subscription === "string" ? this.subscription : undefined) ?? state.subscriptionId;
    const resourceGroup = (typeof this.resourceGroup === "string" ? this.resourceGroup : undefined) ?? state.resourceGroup;
    let webappUrl: string | undefined;
    try {
      const d = await discoverGateway({ subscriptionId, interactive: false, resourceGroup });
      await writeConfig({
        gatewayUrl: d.gatewayUrl, gatewayClientId: d.gatewayClientId, gatewayTenantId: d.gatewayTenantId,
        subscriptionId: d.subscriptionId, containerAppResourceId: d.containerAppResourceId,
        cachedAt: new Date().toISOString(),
      });
      webappUrl = d.gatewayUrl;
      this.context.stdout.write(
        `${colors.success("✅ The platform is live and your local tools are pointed at it.")}\n` +
        `  gateway:  ${d.gatewayUrl}\n` +
        `  foundry:  ${doc.result?.foundryEndpoint ?? "-"}\n\n`,
      );
    } catch (e) {
      const err = e as LocalCliError;
      this.context.stdout.write(`${colors.success("✅ The platform is live.")}\n\n`);
      this.context.stderr.write(
        `  ${colors.hint(`⚠ couldn't auto-point your local tools at the gateway: ${err.message}`)}\n` +
        (err.hint ? `  ${colors.hint(err.hint)}\n` : "") + "\n",
      );
    }

    // End-of-install summary — best-effort read of the brain-App org (basic
    // platform → no github-app.json → no team block).
    let brainOrg: string | null = null;
    try {
      const credsRaw = await fs.readFile(path.join(markerDir, "github-app.json"), "utf8");
      const creds = JSON.parse(credsRaw) as { org?: string };
      brainOrg = typeof creds.org === "string" ? creds.org : null;
    } catch { /* no creds → basic platform */ }
    this.context.stdout.write(renderInstallSummary({ webappUrl, brainOrg }) + "\n");

    this.context.stdout.write(
      `${colors.field("Finish wiring your coding agent:")}\n` +
      `  • Render the m8t personas + MCP into your host — follow guides/install/m8t.md (step 4) and, optionally, guides/install/m8t-stack.md (step 5).\n` +
      `  • Make sure your m8t CLI is current: npm i -g @m8t-stack/cli@latest\n\n` +
      `${colors.field("Then open a brand new chat/session")} — new skills + MCP servers load only on a fresh start.\n`,
    );
    // Reactive company-profile seed — independent of discovery (needs only the endpoint).
    try {
      await reactiveSeedOnFinish({
        endpoint: doc.result?.foundryEndpoint,
        stdout: (s) => this.context.stdout.write(s),
      });
    } catch (e) {
      this.context.stderr.write(
        `  ${colors.dim(`(company-profile seed deferred: ${e instanceof Error ? e.message : String(e)} — run 'm8t bootstrap seed-profile' later)`)}\n`,
      );
    }
    return 0;
  }
}
